// Enhanced Sidebar.js with both bulk and individual player name editing

import React, { useState, useMemo, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { createTournamentManager, detectTournamentFormat } from "../../utils/tournamentManager";
import { getUnusedTables, getCurrentlyUsedTables, getAvailableTables } from "../../utils/tableAssignmentUtils";
import { TOURNAMENT_FORMAT_LABELS, TOURNAMENT_FORMAT_DESCRIPTIONS } from "../../types/tournamentTypes";
import PayoutCalculatorModal from "../features/PayoutCalculatorModal";
import SlowMatchAlert from "./SlowMatchAlert";
import RegistrationsModal from "../common/RegistrationsModal";
import PlayersModal from "./PlayersModal";
import { getCurrentOrNextOccurrence, formatLocalDateTime } from "../../utils/recurringUtils";
import { isTournamentComplete } from "../../utils/layoutUtils";
import { getBracketPlayers } from "../../utils/bracketSchema";
import ReportToFargoModal from "../sharing/ReportToFargoModal";

export default function Sidebar({
    playersText,
    handlePlayersChange,
    handlePlayersBlur,
    handleShuffleBracket,
    shuffleButtonText,
    exportTournament,
    importTournament,
    fileInputRef,
    bracket,
    payoutAmounts,
    setPayoutAmounts,
    numPayoutPlaces,
    setNumPayoutPlaces,
    simulateTournament,
    handleSidebarPlayerNameChange,
    sidebarOpen,
    setSidebarOpen,
    assignTables,
    assignTablesNoShuffle,
    clearTables,
    customTableNumbers,
    setCustomTableNumbers,
    autoReassignTables,
    setAutoReassignTables,
    resetTournamentData,
    tournamentFormat,
    onTournamentFormatChange,
    lastPlayerList,
    tournamentTitle = null,
    tournament = null,
    raceLength = 5,
    setRaceLength,
    expectedTimePerMatch = 30,
    setExpectedTimePerMatch,
    selectedInstanceDate = null,
    playerPayments = {},
    setPlayerPayments = null,
    playerPhones = {},
    onPlayerPhoneChange = null,
    playerUserIds = {},
    onPlayerUserIdChange = null,
    selfScoringEnabled = false
}) {
    const [editingPlayer, setEditingPlayer] = useState(null);
    const [editValue, setEditValue] = useState("");
    const [editError, setEditError] = useState("");
    const [showWarningDialog, setShowWarningDialog] = useState(false);
    const [warningAction, setWarningAction] = useState(null);
    // Text the user must type ("RESET") to confirm the destructive reset action.
    const [resetConfirmText, setResetConfirmText] = useState("");
    const [showPayoutCalculator, setShowPayoutCalculator] = useState(false);
    const [showPlayersModal, setShowPlayersModal] = useState(false);
    const [payoutConfig, setPayoutConfig] = useState(null);
    const [matchSettingsExpanded, setMatchSettingsExpanded] = useState(true);
    const [tableManagementExpanded, setTableManagementExpanded] = useState(true);
    const [availableTablesExpanded, setAvailableTablesExpanded] = useState(true);
    const [payoutExpanded, setPayoutExpanded] = useState(true);

    // Collapse the Available Tables editor once play begins — the director rarely
    // edits the table list mid-tournament, so hiding it declutters the panel while
    // the Table Status grid stays visible. Fires once (on the first started match)
    // so it never fights a manual re-open; the label chevron toggles it back.
    const hasStartedMatch = useMemo(
        () => Array.isArray(bracket) && bracket.some(m => m.started || m.finished),
        [bracket]
    );
    const autoCollapsedTablesRef = useRef(false);
    useEffect(() => {
        if (hasStartedMatch && !autoCollapsedTablesRef.current) {
            autoCollapsedTablesRef.current = true;
            setAvailableTablesExpanded(false);
        }
    }, [hasStartedMatch]);
    const [showRegistrationsModal, setShowRegistrationsModal] = useState(false);
    const [showReportFargo, setShowReportFargo] = useState(false);

    // Format name as "First Last-Initial" (e.g., "Ian Waters" -> "Ian W.")
    // For doubles with "&", format each player separately
    const formatNameWithInitial = (fullName) => {
        if (!fullName || fullName === "BYE") return fullName;

        // Handle doubles format with "&"
        if (fullName.includes(' & ')) {
            const players = fullName.split(' & ');
            return players.map(p => formatSingleName(p)).join(' & ');
        }

        return formatSingleName(fullName);
    };

    // Format a single player name: "First Last" -> "First L."
    const formatSingleName = (name) => {
        if (!name) return name;
        const parts = name.trim().split(/\s+/);
        if (parts.length === 1) return parts[0]; // Single name, no change

        // First name + last initial with period
        const firstName = parts[0];
        const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase() + '.';
        return `${firstName} ${lastInitial}`;
    };

    // Calculate filtered registration count for current instance
    const instanceRegistrationCount = useMemo(() => {
        if (!tournament?.registrations?.registeredUsers) return 0;

        const allUsers = tournament.registrations.registeredUsers;

        // Determine target instance date
        // Use getCurrentOrNextOccurrence to match how RegisterButton calculates the date
        // Use formatLocalDateTime to avoid UTC conversion issues
        let targetInstanceDate = selectedInstanceDate;
        if (!targetInstanceDate && tournament.recurring?.enabled) {
            const currentOrNextDate = getCurrentOrNextOccurrence(tournament.recurring);
            targetInstanceDate = currentOrNextDate ? formatLocalDateTime(currentOrNextDate) : null;
        }

        // Filter registrations for the current instance
        if (!targetInstanceDate) {
            // Non-recurring tournament: show registrations without instanceDate
            return allUsers.filter(reg => !reg.instanceDate).length;
        }
        // Recurring tournament: show registrations for this specific instance
        return allUsers.filter(reg => reg.instanceDate === targetInstanceDate).length;
    }, [tournament?.registrations?.registeredUsers, tournament?.recurring, selectedInstanceDate]);

    // Track last synced registrations to avoid duplicate syncs
    const lastSyncedRegistrations = useRef(null);

    // Check if this is a doubles/scotch doubles tournament
    const isDoublesFormat = tournament?.playerFormat === 'Doubles' || tournament?.playerFormat === 'Scotch Doubles';

    // Auto-sync registered players to player list (only before tournament starts)
    useEffect(() => {
        // Only auto-sync if tournament hasn't started yet and we have registrations
        if (bracket.length > 0) return;
        if (!tournament?.isPublic) return;
        if (!tournament?.registrations?.registeredUsers) return;

        const allUsers = tournament.registrations.registeredUsers;

        // Determine target instance date
        // Use formatLocalDateTime to avoid UTC conversion issues
        let targetInstanceDate = selectedInstanceDate;
        if (!targetInstanceDate && tournament.recurring?.enabled) {
            const currentOrNextDate = getCurrentOrNextOccurrence(tournament.recurring);
            targetInstanceDate = currentOrNextDate ? formatLocalDateTime(currentOrNextDate) : null;
        }

        // Filter registrations for the current instance
        let instanceRegistrations;
        if (!targetInstanceDate) {
            // Non-recurring tournament: show registrations without instanceDate
            instanceRegistrations = allUsers.filter(reg => !reg.instanceDate);
        } else {
            // Recurring tournament: show registrations for this specific instance
            instanceRegistrations = allUsers.filter(reg => reg.instanceDate === targetInstanceDate);
        }

        if (instanceRegistrations.length === 0) return;

        // Create a signature of current registrations to detect changes
        const registrationSignature = instanceRegistrations
            .map(reg => `${reg.userId}-${reg.partnerName || ''}`)
            .sort()
            .join('|');

        // Skip if we've already synced these exact registrations
        if (lastSyncedRegistrations.current === registrationSignature) return;

        // Get player names from registrations
        const registeredPlayerNames = instanceRegistrations.map(reg => {
            if (isDoublesFormat && reg.partnerName) {
                return `${reg.displayName} & ${reg.partnerName}`;
            }
            return reg.displayName;
        });

        // Get current players
        const currentPlayers = playersText.trim().split('\n').filter(Boolean);

        // Filter out players that are already in the list
        const newPlayers = registeredPlayerNames.filter(name =>
            !currentPlayers.some(p => p.trim().toLowerCase() === name.trim().toLowerCase())
        );

        // If there are new players to add
        if (newPlayers.length > 0) {
            // Combine current and new players
            const allPlayers = [...currentPlayers, ...newPlayers];
            const newPlayersText = allPlayers.join('\n');

            // Update players text
            handlePlayersChange({ target: { value: newPlayersText } });
        }

        // Mark these registrations as synced
        lastSyncedRegistrations.current = registrationSignature;
    }, [tournament?.registrations?.registeredUsers, tournament?.recurring, tournament?.isPublic, selectedInstanceDate, bracket.length, playersText, handlePlayersChange, isDoublesFormat]);

    function getPlaceLabel(idx) {
        if (idx === 0) return "First";
        if (idx === 1) return "Second";
        if (idx === 2) return "Third";
        if (idx === 3) return "Fourth";
        if (idx === 4) return "Fifth";
        if (idx === 5) return "Sixth";
        if (idx === 6) return "Seventh";
        if (idx === 7) return "Eighth";
        return `${idx + 1}th`;
    }

    // Get current players from the bracket or sidebar text
    const getCurrentPlayers = () => {
        // If bracket exists, the bracket is the source of truth — extract from it
        // (shared with the persistence path so saved players match the bracket).
        if (bracket.length > 0) {
            return [...getBracketPlayers(bracket)].sort();
        } else {
            // Use sidebar text as fallback when no bracket exists
            return playersText
                .trim()
                .split('\n')
                .filter(Boolean)
                .map(name => name.trim())
                .filter(name => name);
        }
    };

    const handlePlayerEdit = (oldName, newName, playerIndex) => {
        if (!newName.trim() || oldName === newName) return;

        const cleanNewName = newName.trim();
        
        // Check if the new name already exists (excluding the player being edited)
        const isDuplicate = currentPlayers.some((playerName, idx) => {
            return idx !== playerIndex && 
                   playerName.toLowerCase() === cleanNewName.toLowerCase();
        });
        
        if (isDuplicate) {
            // Set error state instead of alert
            setEditError(`Player name "${cleanNewName}" already exists. Please choose a different name.`);
            return;
        }

        // Clear any previous errors
        setEditError("");

        if (handleSidebarPlayerNameChange) {
            handleSidebarPlayerNameChange(oldName, cleanNewName, playerIndex);
        }

        setEditingPlayer(null);
        setEditValue("");
    };

    const startEditing = (playerName) => {
        setEditingPlayer(playerName);
        setEditValue(playerName);
        setEditError(""); // Clear any previous errors
    };

    const cancelEditing = () => {
        setEditingPlayer(null);
        setEditValue("");
        setEditError(""); // Clear errors when canceling
    };

    const submitEdit = (oldName, playerIndex) => {
        handlePlayerEdit(oldName, editValue, playerIndex);
    };

    const handleKeyPress = (e, oldName, playerIndex) => {
        if (e.key === 'Enter') {
            handlePlayerEdit(oldName, editValue, playerIndex);
        } else if (e.key === 'Escape') {
            cancelEditing();
        }
    };

    // Reset tournament function with warning
    const resetTournament = () => {
        setWarningAction(() => () => {
            // Reset tournament but preserve player list
            if (resetTournamentData) {
                resetTournamentData(false); // Pass false to preserve player list
            } else {
                handlePlayersChange({ target: { value: '' } });
            }
            closeWarningDialog();
        });
        setResetConfirmText(""); // Start with an empty confirmation field
        setShowWarningDialog(true);
    };

    // Close the reset warning and clear the typed confirmation text.
    const closeWarningDialog = () => {
        setShowWarningDialog(false);
        setResetConfirmText("");
    };

    // The confirmation passes when the user types "RESET" (case-insensitive,
    // surrounding whitespace ignored) — we're testing intent, not spelling.
    const isResetConfirmed = resetConfirmText.trim().toUpperCase() === 'RESET';

    // Handle payout calculator save
    const handlePayoutConfigSave = (config) => {
        setPayoutConfig(config);
        setNumPayoutPlaces(config.numberOfPlaces);

        // Update payout amounts with calculated values
        const newPayoutAmounts = config.payouts.map(amount => `$${amount}`);
        setPayoutAmounts(newPayoutAmounts);
    };

    // Handle payment checkbox toggle
    const handlePaymentToggle = (playerName) => {
        if (!setPlayerPayments) return;

        setPlayerPayments(prev => {
            const currentStatus = prev[playerName]?.paid || false;
            return {
                ...prev,
                [playerName]: {
                    paid: !currentStatus,
                    timestamp: new Date().toISOString()
                }
            };
        });
    };

    const currentPlayers = getCurrentPlayers();
    const hasBracket = bracket.length > 0;

    return (
        <>
            {/* Mobile overlay when sidebar is open */}
            {window.innerWidth <= 768 && sidebarOpen && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'var(--bg-modal-overlay)',
                        zIndex: 2000,
                        cursor: 'pointer',
                        backdropFilter: 'blur(4px)',
                        WebkitBackdropFilter: 'blur(4px)'
                    }}
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Toggle Button - Enhanced with gradient */}
            <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                style={{
                    position: 'fixed',
                    top: window.innerWidth <= 768 ? '50px' : '70px',
                    left: window.innerWidth <= 768 ? (sidebarOpen ? '268px' : '10px') : (sidebarOpen ? '268px' : '10px'),
                    zIndex: 3000,
                    width: window.innerWidth <= 768 ? '36px' : '40px',
                    height: window.innerWidth <= 768 ? '36px' : '40px',
                    borderRadius: '50%',
                    background: 'var(--accent-gradient)',
                    color: 'var(--text-on-accent)',
                    border: '2px solid rgba(255, 255, 255, 0.2)',
                    boxShadow: 'var(--shadow-md), 0 4px 12px rgba(102, 126, 234, 0.3)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: window.innerWidth <= 768 ? '18px' : '20px',
                    fontWeight: 'bold',
                    transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    fontFamily: 'var(--font-display)'
                }}
                onMouseEnter={(e) => {
                    e.target.style.transform = 'scale(1.1) rotate(5deg)';
                    e.target.style.boxShadow = 'var(--shadow-lg), 0 8px 20px rgba(102, 126, 234, 0.4)';
                }}
                onMouseLeave={(e) => {
                    e.target.style.transform = 'scale(1) rotate(0deg)';
                    e.target.style.boxShadow = 'var(--shadow-md), 0 4px 12px rgba(102, 126, 234, 0.3)';
                }}
                title={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
            >
                <span style={{
                    transition: 'transform 0.3s ease',
                    transform: sidebarOpen ? 'rotate(0deg)' : 'rotate(180deg)',
                    display: 'inline-block'
                }}>
                    ‹
                </span>
            </button>

            {/* Sidebar - Glass Morphism */}
            <div style={{
                position: 'fixed',
                left: sidebarOpen ? 0 : '-280px',
                top: 0,
                width: window.innerWidth <= 768 ? '268px' : '268px',
                height: '100vh',
                background: 'var(--sidebar-bg)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                borderRight: '1px solid var(--border-glass)',
                overflowY: 'auto',
                zIndex: window.innerWidth <= 768 ? 2500 : 1000,
                paddingTop: window.innerWidth <= 768 ? '16px' : '20px',
                paddingLeft: '16px',
                paddingRight: '16px',
                paddingBottom: '100px',
                transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: sidebarOpen ? 'var(--shadow-xl), 0 0 40px rgba(0, 0, 0, 0.1)' : 'none'
            }}>
                {/* Players Section */}
                <div>
                    {hasBracket ? (
                        // Tournament is active - show manage players button
                        <>
                            <button
                                onClick={() => setShowPlayersModal(true)}
                                className="btn-manage-players ripple-effect"
                                style={{
                                    width: '100%',
                                    padding: '18px 16px',
                                    background: 'var(--accent-gradient)',
                                    color: 'var(--text-on-accent)',
                                    border: 'none',
                                    borderRadius: 'var(--radius-lg)',
                                    fontSize: 14,
                                    fontWeight: '700',
                                    fontFamily: 'var(--font-display)',
                                    cursor: 'pointer',
                                    marginBottom: 16,
                                    boxShadow: 'var(--shadow-md), 0 4px 12px rgba(102, 126, 234, 0.25)',
                                    transition: 'all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                    textAlign: 'center',
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-3px)';
                                    e.currentTarget.style.boxShadow = 'var(--shadow-lg), 0 8px 20px rgba(102, 126, 234, 0.35)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = 'var(--shadow-md), 0 4px 12px rgba(102, 126, 234, 0.25)';
                                }}
                            >
                                {/* Noise texture overlay */}
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.65\' numOctaves=\'3\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")',
                                    opacity: 0.03,
                                    pointerEvents: 'none'
                                }} />

                                <div style={{
                                    fontSize: 16,
                                    marginBottom: 6,
                                    letterSpacing: '-0.01em',
                                    position: 'relative'
                                }}>
                                    Manage Players
                                </div>

                                {/* Player count badge */}
                                <div style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    padding: '4px 12px',
                                    background: 'rgba(255, 255, 255, 0.2)',
                                    borderRadius: 'var(--radius-full)',
                                    fontSize: 12,
                                    fontWeight: 600
                                }}>
                                    <span>{currentPlayers.length} player{currentPlayers.length !== 1 ? 's' : ''}</span>
                                    {tournament?.entryFee > 0 && setPlayerPayments && (() => {
                                        const paidCount = currentPlayers.filter(p => playerPayments[p]?.paid).length;
                                        const totalCollected = paidCount * (tournament?.entryFee || 0);
                                        return (
                                            <>
                                                <span style={{ opacity: 0.6 }}>•</span>
                                                <span style={{ color: paidCount === currentPlayers.length ? '#a7f3d0' : 'inherit' }}>
                                                    ${totalCollected}
                                                </span>
                                            </>
                                        );
                                    })()}
                                </div>
                            </button>

                            {false && (
                                <div style={{
                                    marginBottom: 20,
                                    backgroundColor: 'var(--bg-secondary)',
                                    borderRadius: 8,
                                    border: '1px solid var(--border-color)',
                                    padding: '10px'
                                }}>
                                    {/* Header Row */}
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '8px 0',
                                        borderBottom: '2px solid var(--border-color)',
                                        marginBottom: 4
                                    }}>
                                        <div style={{
                                            flex: 1,
                                            fontSize: 13,
                                            fontWeight: 'bold',
                                            color: 'var(--text-primary)'
                                        }}>
                                            Player Name
                                        </div>
                                        {tournament?.entryFee > 0 && setPlayerPayments && (
                                            <div style={{
                                                width: 50,
                                                fontSize: 13,
                                                fontWeight: 'bold',
                                                color: 'var(--text-primary)',
                                                textAlign: 'center'
                                            }}>
                                                Paid
                                            </div>
                                        )}
                                        <div style={{
                                            width: 40,
                                            fontSize: 13,
                                            fontWeight: 'bold',
                                            color: 'var(--text-primary)',
                                            textAlign: 'center'
                                        }}>
                                            Edit
                                        </div>
                                    </div>

                                    {/* Player Rows */}
                                    {currentPlayers.map((playerName, idx) => (
                                        <div key={idx} style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '6px 0',
                                            borderBottom: idx < currentPlayers.length - 1 ? '1px solid var(--border-color)' : 'none'
                                        }}>
                                            {editingPlayer === playerName ? (
                                                <div style={{ width: '100%' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                        <input
                                                            type="text"
                                                            value={editValue}
                                                            onChange={(e) => setEditValue(e.target.value)}
                                                            onKeyDown={(e) => handleKeyPress(e, playerName, idx)}
                                                            style={{
                                                                flex: 1,
                                                                border: editError ? '1px solid var(--danger)' : '1px solid var(--btn-primary)',
                                                                borderRadius: 3,
                                                                padding: '4px 6px',
                                                                fontSize: 13,
                                                                marginRight: 8
                                                            }}
                                                            autoFocus
                                                        />
                                                        <button
                                                            onClick={() => submitEdit(playerName, idx)}
                                                            style={{
                                                                backgroundColor: 'var(--success)',
                                                                border: 'none',
                                                                color: 'var(--bg-secondary)',
                                                                cursor: 'pointer',
                                                                fontSize: 12,
                                                                padding: '4px 8px',
                                                                borderRadius: 3,
                                                                marginRight: 4
                                                            }}
                                                            title="Save changes"
                                                        >
                                                            ✓
                                                        </button>
                                                        <button
                                                            onClick={cancelEditing}
                                                            style={{
                                                                backgroundColor: 'var(--danger)',
                                                                border: 'none',
                                                                color: 'var(--bg-secondary)',
                                                                cursor: 'pointer',
                                                                fontSize: 12,
                                                                padding: '4px 8px',
                                                                borderRadius: 3
                                                            }}
                                                            title="Cancel changes"
                                                        >
                                                            ✗
                                                        </button>
                                                    </div>
                                                    {editError && (
                                                        <div style={{
                                                            color: 'var(--danger)',
                                                            fontSize: 11,
                                                            marginTop: 4
                                                        }}>
                                                            {editError}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <>
                                                    <div style={{
                                                        flex: 1,
                                                        fontSize: 13,
                                                        color: 'var(--text-primary)'
                                                    }}>
                                                        {playerName}
                                                    </div>
                                                    {tournament?.entryFee > 0 && setPlayerPayments && (
                                                        <div style={{
                                                            width: 50,
                                                            display: 'flex',
                                                            justifyContent: 'center'
                                                        }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={playerPayments[playerName]?.paid || false}
                                                                onChange={() => handlePaymentToggle(playerName)}
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    width: 16,
                                                                    height: 16
                                                                }}
                                                                title={playerPayments[playerName]?.paid ? "Mark as unpaid" : "Mark as paid"}
                                                            />
                                                        </div>
                                                    )}
                                                    <div style={{
                                                        width: 40,
                                                        display: 'flex',
                                                        justifyContent: 'center'
                                                    }}>
                                                        <button
                                                            onClick={() => startEditing(playerName)}
                                                            style={{
                                                                background: 'none',
                                                                border: 'none',
                                                                color: 'var(--text-secondary)',
                                                                cursor: 'pointer',
                                                                fontSize: 14,
                                                                padding: '2px 4px'
                                                            }}
                                                            title="Edit player name"
                                                        >
                                                            ✏️
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    ))}
                                    <div style={{
                                        fontSize: 11,
                                        color: 'var(--text-secondary)',
                                        fontStyle: 'italic',
                                        marginTop: 10,
                                        textAlign: 'center'
                                    }}>
                                        Click the edit icon to change player names
                                    </div>

                                    {/* Payment Summary - Only show if entry fee is set */}
                                    {tournament?.entryFee > 0 && setPlayerPayments && (
                                        <div style={{
                                            marginTop: 15,
                                            padding: '10px 12px',
                                            backgroundColor: 'var(--bg-tertiary)',
                                            borderRadius: 6,
                                            border: '1px solid var(--border-color)'
                                        }}>
                                            <div style={{
                                                fontSize: 13,
                                                fontWeight: 'bold',
                                                color: 'var(--text-primary)',
                                                marginBottom: 6
                                            }}>
                                                Payment Summary
                                            </div>
                                            {(() => {
                                                const totalPlayers = currentPlayers.length;
                                                const paidCount = currentPlayers.filter(p => playerPayments[p]?.paid).length;
                                                const totalExpected = totalPlayers * (tournament?.entryFee || 0);
                                                const totalCollected = paidCount * (tournament?.entryFee || 0);

                                                return (
                                                    <>
                                                        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>
                                                            Players: <span style={{ fontWeight: '500', color: 'var(--text-primary)' }}>
                                                                {paidCount} / {totalPlayers} paid
                                                            </span>
                                                        </div>
                                                        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                                                            Total: <span style={{ fontWeight: '500', color: 'var(--success)' }}>
                                                                ${totalCollected}
                                                            </span> / <span style={{ fontWeight: '500', color: 'var(--text-primary)' }}>
                                                                ${totalExpected}
                                                            </span> collected
                                                        </div>
                                                    </>
                                                );
                                            })()}
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    ) : (
                        // Pre-tournament - show manage players button with pulse ring when empty
                        <>
                            <button
                                onClick={() => setShowPlayersModal(true)}
                                className={`btn-manage-players ripple-effect ${currentPlayers.length === 0 ? 'animate-pulse-ring' : ''}`}
                                style={{
                                    width: '100%',
                                    padding: '18px 16px',
                                    background: 'var(--accent-gradient)',
                                    color: 'var(--text-on-accent)',
                                    border: 'none',
                                    borderRadius: 'var(--radius-lg)',
                                    fontSize: 14,
                                    fontWeight: '700',
                                    fontFamily: 'var(--font-display)',
                                    cursor: 'pointer',
                                    marginBottom: 16,
                                    boxShadow: 'var(--shadow-md), 0 4px 12px rgba(102, 126, 234, 0.25)',
                                    transition: 'all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                    textAlign: 'center',
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-3px)';
                                    e.currentTarget.style.boxShadow = 'var(--shadow-lg), 0 8px 20px rgba(102, 126, 234, 0.35)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = 'var(--shadow-md), 0 4px 12px rgba(102, 126, 234, 0.25)';
                                }}
                            >
                                {/* Noise texture overlay */}
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.65\' numOctaves=\'3\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")',
                                    opacity: 0.03,
                                    pointerEvents: 'none'
                                }} />

                                <div style={{
                                    fontSize: 16,
                                    marginBottom: currentPlayers.length > 0 ? 6 : 4,
                                    letterSpacing: '-0.01em',
                                    position: 'relative'
                                }}>
                                    {currentPlayers.length === 0 ? '+ Add Players' : 'Manage Players'}
                                </div>

                                {currentPlayers.length > 0 && (
                                    <div style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        padding: '4px 12px',
                                        background: 'rgba(255, 255, 255, 0.2)',
                                        borderRadius: 'var(--radius-full)',
                                        fontSize: 12,
                                        fontWeight: 600
                                    }}>
                                        <span>{currentPlayers.length} player{currentPlayers.length !== 1 ? 's' : ''}</span>
                                        {tournament?.entryFee > 0 && setPlayerPayments && (() => {
                                            const paidCount = currentPlayers.filter(p => playerPayments[p]?.paid).length;
                                            const totalCollected = paidCount * (tournament?.entryFee || 0);
                                            return (
                                                <>
                                                    <span style={{ opacity: 0.6 }}>•</span>
                                                    <span style={{ color: paidCount === currentPlayers.length ? '#a7f3d0' : 'inherit' }}>
                                                        ${totalCollected}
                                                    </span>
                                                </>
                                            );
                                        })()}
                                    </div>
                                )}

                                {currentPlayers.length === 0 && (
                                    <div style={{
                                        fontSize: 12,
                                        opacity: 0.85,
                                        position: 'relative'
                                    }}>
                                        Click to get started
                                    </div>
                                )}
                            </button>
                        </>
                    )}
                </div>

                {/* Registrations Section - public tournaments only, and only before
                    play begins: its purpose is pulling sign-ups into the bracket,
                    which is no longer possible once a match has started (the same
                    roster is still viewable via Manage Players). */}
                {tournament?.isPublic && tournament?.registrations && !hasStartedMatch && (
                    <div style={{ marginBottom: 16, width: '100%' }}>
                        <button
                            onClick={() => setShowRegistrationsModal(true)}
                            style={{
                                width: '100%',
                                padding: '12px 16px',
                                backgroundColor: 'var(--accent-primary)',
                                color: 'var(--bg-secondary)',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#5568d3';
                                e.target.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'var(--accent-primary)';
                                e.target.style.transform = 'translateY(0)';
                            }}
                        >
                            View Registrations
                            {instanceRegistrationCount > 0 && (
                                <span style={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                    padding: '2px 8px',
                                    borderRadius: '12px',
                                    fontSize: '12px'
                                }}>
                                    {instanceRegistrationCount}
                                </span>
                            )}
                        </button>
                    </div>
                )}

                {/* Action Buttons */}
                {(() => {
                    // Disable shuffle if any non-BYE match has started or finished
                    // BYE matches are auto-finalized and shouldn't prevent reshuffling
                    const isByeMatch = (m) => m.p1?.name === "BYE" || m.p2?.name === "BYE";
                    const anyRealMatchStarted = bracket.some(m => (m.started || m.finished) && !isByeMatch(m));
                    const isDisabled = anyRealMatchStarted && bracket.length > 0;
                    return (
                        <button
                            onClick={handleShuffleBracket}
                            disabled={isDisabled}
                            style={{
                                ...buttonStyle,
                                backgroundColor: shuffleButtonText === 'Start Tournament' ? 'var(--success)' : 'var(--accent-primary)',
                                boxShadow: 'var(--shadow-sm)',
                                opacity: isDisabled ? 0.5 : 1,
                                cursor: isDisabled ? 'not-allowed' : 'pointer'
                            }}
                            onMouseEnter={(e) => {
                                if (!isDisabled) {
                                    e.target.style.transform = 'translateY(-2px)';
                                    e.target.style.boxShadow = 'var(--shadow-md)';
                                }
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = 'var(--shadow-sm)';
                            }}
                        >
                            {shuffleButtonText}
                        </button>
                    );
                })()}


                {/* Reset Tournament Button - only show if tournament exists */}
                {bracket.length > 0 && (
                    <button
                        onClick={resetTournament}
                        style={{
                            ...buttonStyle,
                            backgroundColor: "var(--danger)",
                            marginBottom: 20
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = 'var(--shadow-md)';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = 'var(--shadow-sm)';
                        }}
                    >
                        Reset Tournament
                    </button>
                )}

                {/* Report to Fargo — only for a completed cloud tournament */}
                {tournament?.id && bracket && bracket.length > 0 &&
                  isTournamentComplete(bracket, tournamentFormat) && (
                    <button
                        onClick={() => setShowReportFargo(true)}
                        title="Generate a FargoRate results report for this completed tournament"
                        style={{
                            width: '100%', padding: '10px 16px', backgroundColor: 'var(--accent-primary)',
                            color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: 700,
                            cursor: 'pointer', marginBottom: 20, display: 'flex', alignItems: 'center',
                            justifyContent: 'center', gap: '6px'
                        }}
                    >
                        📤 Report to Fargo
                    </button>
                )}
                {showReportFargo && tournament?.id && (
                    <ReportToFargoModal
                        tournament={tournament}
                        // For a recurring occurrence, report the live bracket that's open
                        // in the editor (the occurrence lives in a subcollection now, not
                        // the parent `instances` map). Non-recurring reports the root doc.
                        instanceData={selectedInstanceDate ? { bracket, playerUserIds } : null}
                        instanceDate={selectedInstanceDate || null}
                        onClose={() => setShowReportFargo(false)}
                    />
                )}

                <input
                    type="file"
                    accept="application/json"
                    style={{ display: "none" }}
                    ref={fileInputRef}
                    onChange={importTournament}
                />

                {/* Slow Match Alert - Show if tournament is active */}
                {hasBracket && tournament && (
                    <SlowMatchAlert
                        bracket={bracket}
                        tournament={tournament}
                        isMobile={window.innerWidth <= 768}
                    />
                )}

                {/* Match Settings Section - Only show before tournament starts */}
                {bracket.length === 0 && (
                <div style={{ ...glassBoxStyle, marginTop: 20, marginBottom: 16 }}>
                    <div
                        className="sidebar-section-header"
                        style={{
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            userSelect: 'none',
                            padding: '10px 12px',
                            margin: '-8px -12px 0 -12px',
                            borderRadius: 'var(--radius-md)',
                            transition: 'all 0.2s ease'
                        }}
                        onClick={() => setMatchSettingsExpanded(!matchSettingsExpanded)}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                        <span style={{
                            fontFamily: 'var(--font-display)',
                            fontWeight: 700,
                            fontSize: 14,
                            color: 'var(--text-primary)',
                            letterSpacing: '-0.01em'
                        }}>Match Settings</span>
                        <span style={{
                            width: 20,
                            height: 20,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'var(--accent-primary)',
                            transition: 'transform 0.3s ease',
                            transform: matchSettingsExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                        }}>
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </span>
                    </div>

                    {matchSettingsExpanded && (
                        <div style={{ marginTop: 16, animation: 'fadeIn 0.2s ease-out' }}>
                            {/* Tournament Format Selector */}
                            <div style={{ marginBottom: 16 }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: 8,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: 'var(--text-secondary)',
                                    fontFamily: 'var(--font-display)',
                                    letterSpacing: '0.02em',
                                    textTransform: 'uppercase'
                                }}>
                                    Tournament Format
                                </label>
                                <select
                                    value={tournamentFormat}
                                    onChange={(e) => {
                                        if (bracket.length > 0) {
                                            if (window.confirm('Changing the format will reset the bracket. Continue?')) {
                                                onTournamentFormatChange(e.target.value);
                                            }
                                        } else {
                                            onTournamentFormatChange(e.target.value);
                                        }
                                    }}
                                    style={{
                                        width: '100%',
                                        padding: '10px 12px',
                                        fontSize: 14,
                                        fontWeight: 500,
                                        borderRadius: 'var(--radius-md)',
                                        border: '2px solid var(--border-color)',
                                        boxSizing: 'border-box',
                                        backgroundColor: 'var(--bg-secondary)',
                                        color: 'var(--text-primary)',
                                        cursor: 'pointer',
                                        transition: 'border-color 0.2s ease',
                                        outline: 'none'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = 'var(--accent-primary)'}
                                    onBlur={(e) => e.target.style.borderColor = 'var(--border-color)'}
                                >
                                    {Object.entries(TOURNAMENT_FORMAT_LABELS).map(([value, label]) => (
                                        <option key={value} value={value}>
                                            {label}
                                        </option>
                                    ))}
                                </select>
                                <div style={{
                                    fontSize: 11,
                                    color: 'var(--text-muted)',
                                    marginTop: 6,
                                    lineHeight: 1.4
                                }}>
                                    {TOURNAMENT_FORMAT_DESCRIPTIONS[tournamentFormat]}
                                </div>
                            </div>

                            {/* Expected Match Time */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: 8,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: 'var(--text-secondary)',
                                    fontFamily: 'var(--font-display)',
                                    letterSpacing: '0.02em',
                                    textTransform: 'uppercase'
                                }}>
                                    Match Time (min)
                                </label>
                                <input
                                    type="number"
                                    value={expectedTimePerMatch}
                                    onChange={(e) => {
                                        const value = Math.max(1, Math.min(120, parseInt(e.target.value) || 30));
                                        setExpectedTimePerMatch(value);
                                    }}
                                    min="1"
                                    max="120"
                                    step="1"
                                    style={{
                                        width: '100%',
                                        padding: '10px 12px',
                                        fontSize: 14,
                                        fontFamily: 'var(--font-mono)',
                                        fontWeight: 600,
                                        borderRadius: 'var(--radius-md)',
                                        border: '2px solid var(--border-color)',
                                        boxSizing: 'border-box',
                                        backgroundColor: 'var(--bg-secondary)',
                                        color: 'var(--text-primary)',
                                        transition: 'border-color 0.2s ease',
                                        outline: 'none'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = 'var(--accent-primary)'}
                                    onBlur={(e) => e.target.style.borderColor = 'var(--border-color)'}
                                />
                                <div style={{
                                    fontSize: 11,
                                    color: 'var(--text-muted)',
                                    marginTop: 6
                                }}>
                                    Alerts shown when matches exceed this time
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                )}

                {/* Tables Section */}
                <div style={{ ...glassBoxStyle, marginTop: 16, marginBottom: 16 }}>
                    <div
                        className="sidebar-section-header"
                        style={{
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            userSelect: 'none',
                            padding: '10px 12px',
                            margin: '-8px -12px 0 -12px',
                            borderRadius: 'var(--radius-md)',
                            transition: 'all 0.2s ease'
                        }}
                        onClick={() => setTableManagementExpanded(!tableManagementExpanded)}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                        <span style={{
                            fontFamily: 'var(--font-display)',
                            fontWeight: 700,
                            fontSize: 14,
                            color: 'var(--text-primary)',
                            letterSpacing: '-0.01em'
                        }}>Tables</span>
                        <span style={{
                            width: 20,
                            height: 20,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'var(--accent-primary)',
                            transition: 'transform 0.3s ease',
                            transform: tableManagementExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                        }}>
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </span>
                    </div>

                    {tableManagementExpanded && (
                        <div style={{ marginTop: 16, animation: 'fadeIn 0.2s ease-out' }}>
                            {/* Manual table entry mode */}
                            <div style={{ marginBottom: 14 }}>
                                <div
                                    onClick={() => setAvailableTablesExpanded(v => !v)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        marginBottom: availableTablesExpanded ? 8 : 0,
                                        fontSize: 13,
                                        fontWeight: 600,
                                        color: 'var(--text-secondary)',
                                        fontFamily: 'var(--font-display)',
                                        letterSpacing: '0.02em',
                                        textTransform: 'uppercase',
                                        cursor: 'pointer',
                                        userSelect: 'none'
                                    }}
                                >
                                    <span>Available Tables{!availableTablesExpanded && customTableNumbers ? ` (${customTableNumbers})` : ''}</span>
                                    <span style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'var(--accent-primary)',
                                        transition: 'transform 0.3s ease',
                                        transform: availableTablesExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                                    }}>
                                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </span>
                                </div>
                                {availableTablesExpanded && (
                                    <textarea
                                        value={customTableNumbers}
                                        onChange={(e) => setCustomTableNumbers(e.target.value)}
                                        placeholder="1, 2, 3, 4, 5..."
                                        rows={2}
                                        style={{
                                            width: '100%',
                                            fontSize: 17,
                                            fontFamily: 'var(--font-mono)',
                                            fontWeight: 600,
                                            // Slashed zero so table "0" isn't confused with "8"/"O".
                                            fontVariantNumeric: 'slashed-zero',
                                            border: '2px solid var(--border-color)',
                                            borderRadius: 'var(--radius-md)',
                                            padding: '10px 12px',
                                            resize: 'vertical',
                                            boxSizing: 'border-box',
                                            backgroundColor: 'var(--bg-secondary)',
                                            color: 'var(--text-primary)',
                                            transition: 'border-color 0.2s ease',
                                            outline: 'none'
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = 'var(--accent-primary)'}
                                        onBlur={(e) => e.target.style.borderColor = 'var(--border-color)'}
                                    />
                                )}
                            </div>

                            {/* Auto-reassign tables checkbox */}
                            <div style={{ marginBottom: 14 }}>
                                <label style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    fontSize: 13,
                                    fontWeight: 500,
                                    color: 'var(--text-primary)',
                                    padding: '8px 10px',
                                    borderRadius: 'var(--radius-md)',
                                    backgroundColor: autoReassignTables ? 'var(--accent-primary-light)' : 'transparent',
                                    border: `1px solid ${autoReassignTables ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                                    transition: 'all 0.2s ease'
                                }}>
                                    <input
                                        type="checkbox"
                                        checked={autoReassignTables}
                                        onChange={(e) => {
                                            const isChecked = e.target.checked;
                                            setAutoReassignTables(isChecked);
                                            if (isChecked && bracket.length > 0 && assignTablesNoShuffle) {
                                                setTimeout(() => assignTablesNoShuffle(), 100);
                                            }
                                        }}
                                        style={{
                                            marginRight: '10px',
                                            width: 16,
                                            height: 16,
                                            accentColor: 'var(--accent-primary)'
                                        }}
                                    />
                                    <span>Auto-assign tables</span>
                                </label>
                            </div>

                            {bracket.length > 0 && (
                                <div style={{ display: 'flex', gap: '8px', marginBottom: 14 }}>
                                    <button
                                        onClick={assignTables}
                                        style={{
                                            ...compactButtonStyle,
                                            flex: 1
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                            e.currentTarget.style.boxShadow = 'var(--shadow-md)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
                                        }}
                                    >
                                        Assign
                                    </button>
                                    <button
                                        onClick={clearTables}
                                        style={{
                                            ...compactButtonStyle,
                                            flex: 1,
                                            background: 'transparent',
                                            color: 'var(--danger)',
                                            border: '2px solid var(--danger)'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = 'var(--danger)';
                                            e.currentTarget.style.color = 'var(--text-on-accent)';
                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = 'transparent';
                                            e.currentTarget.style.color = 'var(--danger)';
                                            e.currentTarget.style.transform = 'translateY(0)';
                                        }}
                                    >
                                        Clear
                                    </button>
                                </div>
                            )}

                            {/* Available Tables Display - Enhanced Grid */}
                            {bracket.length > 0 && customTableNumbers.trim() && (() => {
                                const tableSettings = { customTableNumbers };
                                const unusedTables = getUnusedTables(tableSettings, bracket);
                                const usedTables = getCurrentlyUsedTables(bracket);
                                const allTables = getAvailableTables(tableSettings);

                                if (allTables.length === 0) return null;

                                return (
                                    <div style={{
                                        padding: '12px',
                                        backgroundColor: 'var(--bg-tertiary)',
                                        borderRadius: 'var(--radius-md)',
                                        border: '1px solid var(--border-light)'
                                    }}>
                                        <div style={{
                                            fontSize: 11,
                                            fontWeight: 600,
                                            marginBottom: 10,
                                            color: 'var(--text-muted)',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em'
                                        }}>
                                            Table Status
                                        </div>
                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(auto-fill, minmax(44px, 1fr))',
                                            gap: 8
                                        }}>
                                            {allTables.map(table => {
                                                const isUsed = usedTables.has(table);
                                                return (
                                                    <div
                                                        key={table}
                                                        className={!isUsed ? 'animate-table-available' : ''}
                                                        style={{
                                                            width: 44,
                                                            height: 44,
                                                            borderRadius: 'var(--radius-sm)',
                                                            fontSize: 18,
                                                            fontFamily: 'var(--font-mono)',
                                                            fontVariantNumeric: 'slashed-zero',
                                                            fontWeight: 700,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            backgroundColor: isUsed ? 'var(--warning-bg)' : 'var(--success-bg)',
                                                            color: isUsed ? 'var(--warning)' : 'var(--success)',
                                                            border: `1px solid ${isUsed ? 'var(--warning)' : 'var(--success)'}`,
                                                            transition: 'all 0.2s ease'
                                                        }}
                                                        title={isUsed ? 'Table in use' : 'Table available'}
                                                    >
                                                        {table}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 8,
                                            marginTop: 12,
                                            fontSize: 13,
                                            color: 'var(--text-muted)'
                                        }}>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <span style={{
                                                    width: 10,
                                                    height: 10,
                                                    borderRadius: 2,
                                                    backgroundColor: 'var(--success)'
                                                }}></span>
                                                Available ({unusedTables.length})
                                            </span>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <span style={{
                                                    width: 10,
                                                    height: 10,
                                                    borderRadius: 2,
                                                    backgroundColor: 'var(--warning)'
                                                }}></span>
                                                In Use ({usedTables.size})
                                            </span>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    )}
                </div>


                {/* Payout Section */}
                <div style={{ ...glassBoxStyle, marginBottom: 16 }}>
                    <div
                        className="sidebar-section-header"
                        style={{
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            userSelect: 'none',
                            padding: '10px 12px',
                            margin: '-8px -12px 0 -12px',
                            borderRadius: 'var(--radius-md)',
                            transition: 'all 0.2s ease'
                        }}
                        onClick={() => setPayoutExpanded(!payoutExpanded)}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                        <span style={{
                            fontFamily: 'var(--font-display)',
                            fontWeight: 700,
                            fontSize: 14,
                            color: 'var(--text-primary)',
                            letterSpacing: '-0.01em'
                        }}>Payouts</span>
                        <span style={{
                            width: 20,
                            height: 20,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'var(--accent-primary)',
                            transition: 'transform 0.3s ease',
                            transform: payoutExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                        }}>
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </span>
                    </div>

                    {payoutExpanded && (
                        <div style={{ marginTop: 16, animation: 'fadeIn 0.2s ease-out' }}>
                            {/* Calculate Button */}
                            <button
                                onClick={() => setShowPayoutCalculator(true)}
                                style={{
                                    ...compactButtonStyle,
                                    width: '100%',
                                    marginBottom: 14
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                    e.currentTarget.style.boxShadow = 'var(--shadow-md)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
                                }}
                            >
                                Calculate Payouts
                            </button>

                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                marginBottom: 14,
                                padding: '8px 12px',
                                backgroundColor: 'var(--bg-tertiary)',
                                borderRadius: 'var(--radius-md)'
                            }}>
                                <span style={{
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: 'var(--text-secondary)'
                                }}>Places Paid</span>
                                <input
                                    type="number"
                                    min={1}
                                    max={16}
                                    value={numPayoutPlaces}
                                    style={{
                                        width: 50,
                                        fontSize: 14,
                                        fontFamily: 'var(--font-mono)',
                                        fontWeight: 700,
                                        borderRadius: 'var(--radius-sm)',
                                        border: '2px solid var(--border-color)',
                                        textAlign: 'center',
                                        padding: '4px',
                                        backgroundColor: 'var(--bg-secondary)',
                                        color: 'var(--text-primary)'
                                    }}
                                    onChange={e => {
                                        let n = Math.max(1, Math.min(16, Number(e.target.value)));
                                        setNumPayoutPlaces(n);
                                        setPayoutAmounts(prev => {
                                            let next = prev.slice(0, n);
                                            while (next.length < n) next.push("");
                                            return next;
                                        });
                                    }}
                                />
                            </div>

                            {/* Podium Cards */}
                            <div className="payout-podium" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {(() => {
                                    let currentStandings = [];
                                    let finalizedPositions = new Set();

                                    if (bracket && bracket.length > 0) {
                                        try {
                                            const detectedFormat = detectTournamentFormat(bracket) || tournamentFormat;
                                            const tournamentManager = createTournamentManager(detectedFormat);
                                            currentStandings = tournamentManager.getStandings(bracket) || [];

                                            const gf = bracket.find(m => m.bracket === "GF" && !m.id.includes("reset"));
                                            const gfReset = bracket.find(m => m.id === "gf-1-reset");
                                            const gfMatchNum = gf?.matchNum || 0;

                                            const playerLosses = new Map();
                                            const sortedMatches = [...bracket]
                                                .filter(m => m.finished && m.winner)
                                                .sort((a, b) => (a.matchNum || 0) - (b.matchNum || 0));

                                            sortedMatches.forEach(match => {
                                                const loser = match.p1?.name === match.winner ? match.p2?.name : match.p1?.name;
                                                if (loser && !loser.includes("Winner of") && !loser.includes("Loser of") && loser !== "BYE") {
                                                    const current = playerLosses.get(loser) || { losses: 0, eliminationMatchNum: null };
                                                    current.losses++;
                                                    if (current.losses === 2 && current.eliminationMatchNum === null) {
                                                        current.eliminationMatchNum = match.matchNum;
                                                    }
                                                    playerLosses.set(loser, current);
                                                }
                                            });

                                            if (gfReset && gfReset.finished && gfReset.winner && !gfReset.winner.includes('Winner of')) {
                                                finalizedPositions.add(0);
                                                finalizedPositions.add(1);
                                            } else if (gf && gf.finished && gf.winner && !gf.winner.includes('Winner of')) {
                                                const winnerStats = playerLosses.get(gf.winner);
                                                const winnerLosses = winnerStats ? winnerStats.losses : 0;
                                                if (winnerLosses === 0) {
                                                    finalizedPositions.add(0);
                                                    finalizedPositions.add(1);
                                                }
                                            }

                                            playerLosses.forEach((stats) => {
                                                if (stats.losses >= 2 && stats.eliminationMatchNum !== null) {
                                                    const placement = (gfMatchNum - stats.eliminationMatchNum) + 2;
                                                    if (placement >= 3 && placement <= numPayoutPlaces) {
                                                        finalizedPositions.add(placement - 1);
                                                    }
                                                }
                                            });

                                            if (bracket.some(m => m.bracket === 'SE')) {
                                                const seFinal = bracket.find(m => m.bracket === 'SE' && !m.nextMatchId);
                                                if (seFinal && seFinal.finished) {
                                                    for (let i = 0; i < numPayoutPlaces; i++) {
                                                        finalizedPositions.add(i);
                                                    }
                                                }
                                            }
                                        } catch (e) { /* Standings not available yet */ }
                                    }

                                    const playersByPlace = [];
                                    let placeIndex = 0;
                                    for (let i = 0; i < currentStandings.length && placeIndex < numPayoutPlaces; i++) {
                                        const group = currentStandings[i];
                                        for (let j = 0; j < group.length && placeIndex < numPayoutPlaces; j++) {
                                            playersByPlace[placeIndex] = group[j];
                                            placeIndex++;
                                        }
                                    }

                                    const podiumStyles = [
                                        { bg: 'linear-gradient(135deg, var(--gold-bg) 0%, rgba(251, 191, 36, 0.15) 100%)', border: 'var(--gold-border)', icon: '🥇', iconBg: 'var(--gold)' },
                                        { bg: 'linear-gradient(135deg, var(--silver-bg) 0%, rgba(156, 163, 175, 0.15) 100%)', border: 'var(--silver-border)', icon: '🥈', iconBg: 'var(--silver)' },
                                        { bg: 'linear-gradient(135deg, var(--bronze-bg) 0%, rgba(217, 119, 6, 0.15) 100%)', border: 'var(--bronze-border)', icon: '🥉', iconBg: 'var(--bronze)' }
                                    ];

                                    return Array.from({ length: numPayoutPlaces }).map((_, idx) => {
                                        const isFinalized = finalizedPositions.has(idx);
                                        const playerName = isFinalized ? playersByPlace[idx] : null;
                                        const isPodium = idx < 3;
                                        const style = podiumStyles[idx] || {};

                                        return (
                                            <div
                                                key={idx}
                                                className={isPodium && isFinalized ? 'animate-winner-highlight' : ''}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 8,
                                                    padding: '8px 10px',
                                                    borderRadius: 'var(--radius-md)',
                                                    background: isPodium ? style.bg : 'var(--bg-tertiary)',
                                                    border: `1px solid ${isPodium ? style.border : 'var(--border-color)'}`,
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                {/* Player Name & Place Label */}
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{
                                                        fontSize: 10,
                                                        fontWeight: 600,
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.05em',
                                                        color: 'var(--text-muted)',
                                                        marginBottom: 2
                                                    }}>
                                                        {getPlaceLabel(idx)}
                                                    </div>
                                                    <div
                                                        title={playerName || ''}
                                                        style={{
                                                        fontSize: 13,
                                                        fontWeight: playerName ? 700 : 400,
                                                        color: playerName ? 'var(--text-primary)' : 'var(--text-muted)',
                                                        fontStyle: playerName ? 'normal' : 'italic',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        {playerName ? formatNameWithInitial(playerName) : 'TBD'}
                                                    </div>
                                                </div>

                                                {/* Payout Amount Input */}
                                                <input
                                                    type="text"
                                                    value={payoutAmounts[idx] || ""}
                                                    placeholder="$0"
                                                    style={{
                                                        width: 48,
                                                        fontSize: 12,
                                                        fontFamily: 'var(--font-mono)',
                                                        fontWeight: 600,
                                                        borderRadius: 'var(--radius-sm)',
                                                        border: '1px solid var(--border-color)',
                                                        textAlign: 'right',
                                                        padding: '4px 6px',
                                                        backgroundColor: 'var(--bg-secondary)',
                                                        color: payoutAmounts[idx] ? 'var(--success)' : 'var(--text-muted)',
                                                        transition: 'all 0.2s ease',
                                                        outline: 'none'
                                                    }}
                                                    onFocus={(e) => e.target.style.borderColor = 'var(--accent-primary)'}
                                                    onBlur={(e) => e.target.style.borderColor = 'var(--border-color)'}
                                                    onChange={e => {
                                                        const v = e.target.value;
                                                        setPayoutAmounts(payoutAmounts => {
                                                            const arr = payoutAmounts.slice();
                                                            arr[idx] = v;
                                                            return arr;
                                                        });
                                                    }}
                                                />
                                            </div>
                                        );
                                    });
                                })()}
                            </div>
                        </div>
                    )}
                </div>

                {/* Warning Dialog - rendered via portal to center on full screen */}
                {showWarningDialog && ReactDOM.createPortal(
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'var(--bg-modal-overlay)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 10000
                        }}
                        onClick={closeWarningDialog}
                    >
                        <div
                            style={{
                                backgroundColor: 'var(--bg-secondary)',
                                padding: '30px',
                                borderRadius: '10px',
                                boxShadow: 'var(--shadow-lg)',
                                maxWidth: '400px',
                                width: '90%',
                                textAlign: 'center'
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h3 style={{
                                color: 'var(--danger)',
                                marginBottom: '15px',
                                fontSize: '18px'
                            }}>
                                Reset Tournament?
                            </h3>
                            <p style={{
                                marginBottom: '20px',
                                lineHeight: '1.5',
                                color: 'var(--text-primary)'
                            }}>
                                This permanently clears all match results and bracket
                                progress. This <strong>cannot be undone</strong>.
                            </p>
                            <p style={{
                                marginBottom: '10px',
                                fontSize: '14px',
                                color: 'var(--text-secondary)'
                            }}>
                                Type <strong>RESET</strong> to confirm.
                            </p>
                            <input
                                type="text"
                                value={resetConfirmText}
                                onChange={(e) => setResetConfirmText(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && isResetConfirmed && warningAction) {
                                        warningAction();
                                    } else if (e.key === 'Escape') {
                                        closeWarningDialog();
                                    }
                                }}
                                autoFocus
                                placeholder="RESET"
                                aria-label="Type RESET to confirm"
                                style={{
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    padding: '10px 12px',
                                    marginBottom: '20px',
                                    fontSize: '15px',
                                    textAlign: 'center',
                                    letterSpacing: '1px',
                                    border: `2px solid ${isResetConfirmed ? 'var(--danger)' : 'var(--border-color)'}`,
                                    borderRadius: '5px',
                                    backgroundColor: 'var(--bg-primary)',
                                    color: 'var(--text-primary)',
                                    outline: 'none'
                                }}
                            />
                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                                <button
                                    onClick={closeWarningDialog}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: 'var(--btn-primary, #4f46e5)',
                                        color: '#ffffff',
                                        border: 'none',
                                        borderRadius: '5px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => isResetConfirmed && warningAction && warningAction()}
                                    disabled={!isResetConfirmed}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: isResetConfirmed ? 'var(--danger)' : 'var(--btn-disabled, #d1d5db)',
                                        color: isResetConfirmed ? 'var(--bg-secondary)' : 'var(--text-disabled, #9ca3af)',
                                        border: 'none',
                                        borderRadius: '5px',
                                        cursor: isResetConfirmed ? 'pointer' : 'not-allowed',
                                        fontSize: '14px',
                                        opacity: isResetConfirmed ? 1 : 0.7
                                    }}
                                >
                                    Reset
                                </button>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}

                {/* Payout Calculator Modal */}
                {showPayoutCalculator && (
                    <PayoutCalculatorModal
                        onClose={() => setShowPayoutCalculator(false)}
                        onSave={handlePayoutConfigSave}
                        initialPayoutConfig={{
                            ...payoutConfig,
                            playerCount: currentPlayers.length,
                            numberOfPlaces: numPayoutPlaces,
                            buyIn: tournament?.entryFee || payoutConfig?.buyIn || 0,
                            greenFees: tournament?.adminFee || payoutConfig?.greenFees || 0
                        }}
                    />
                )}

                {/* Registrations Modal */}
                {showRegistrationsModal && (
                    <RegistrationsModal
                        isOpen={showRegistrationsModal}
                        onClose={() => setShowRegistrationsModal(false)}
                        tournament={tournament}
                        isHost={true}
                        instanceDate={selectedInstanceDate}
                    />
                )}

                {/* Players Modal */}
                <PlayersModal
                    isOpen={showPlayersModal}
                    onClose={() => setShowPlayersModal(false)}
                    playersText={playersText}
                    handlePlayersChange={handlePlayersChange}
                    handlePlayersBlur={handlePlayersBlur}
                    currentPlayers={currentPlayers}
                    tournament={tournament}
                    playerPayments={playerPayments}
                    handlePaymentToggle={handlePaymentToggle}
                    handleSidebarPlayerNameChange={handleSidebarPlayerNameChange}
                    bracket={bracket}
                    playerPhones={playerPhones}
                    onPlayerPhoneChange={onPlayerPhoneChange}
                    playerUserIds={playerUserIds}
                    onPlayerUserIdChange={onPlayerUserIdChange}
                    selectedInstanceDate={selectedInstanceDate}
                />
            </div>
        </>
    );
}

const buttonStyle = {
    padding: "12px 20px",
    fontSize: 14,
    fontWeight: 700,
    fontFamily: "var(--font-display)",
    borderRadius: "var(--radius-md)",
    background: "var(--accent-gradient)",
    color: "var(--text-on-accent)",
    border: "none",
    boxShadow: "var(--shadow-sm), 0 2px 8px rgba(102, 126, 234, 0.2)",
    cursor: "pointer",
    display: "block",
    width: '100%',
    marginBottom: 10,
    transition: "all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)",
    boxSizing: "border-box",
    letterSpacing: "-0.01em"
};

const compactButtonStyle = {
    padding: "10px 16px",
    fontSize: 13,
    fontWeight: 700,
    fontFamily: "var(--font-display)",
    borderRadius: "var(--radius-md)",
    background: "var(--accent-gradient)",
    color: "var(--text-on-accent)",
    border: "none",
    boxShadow: "var(--shadow-sm)",
    cursor: "pointer",
    transition: "all 0.2s ease",
    boxSizing: "border-box"
};

const glassBoxStyle = {
    marginTop: 16,
    background: "var(--sidebar-section-bg)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    border: "1px solid var(--border-glass)",
    borderRadius: "var(--radius-lg)",
    boxShadow: "var(--shadow-glass)",
    padding: 16,
    width: '100%',
    color: "var(--text-primary)",
    boxSizing: "border-box",
    transition: "all 0.2s ease"
};